import { handleImageLoadError } from '../modules/image';
import { syncFavoriteRemote, refreshLotBidsRemote } from '../modules/api-operations';
import { updateFavoriteCellInPlace } from '../modules/polling';
import { handleDeleteLot } from '../modules/bulkOperations';
import { beginEstimateEdit } from '../modules/estimates';
import { toggleGroupCollapsed } from '../modules/auctionGrouping';
import { openCloseTimeModal, openImagesModal, openLotDetailsModal, showToast } from '../modules/ui';
import { getEffectiveCloseTime, setCustomCloseTime } from '../modules/storage';
import { applyRemoteSnapshot, mapRemoteDocument } from '../modules/lotOperations';
import type { EventHandlerDeps } from './globalEventTypes';

export function setupRowInteractionHandlers(deps: EventHandlerDeps): void {
  const { state, els, render, ensureAccessToken, cfg, DEBUG } = deps;

  if (els.rows) {
    els.rows.addEventListener('error', handleImageLoadError, true);

    els.rows.addEventListener(
      'click',
      (e: Event) => {
        const startEl = getEventTarget(e);
        if (!startEl) return;

        handleFavoriteClick(e, startEl, state, render, ensureAccessToken);
        handleDeleteClick(e, startEl, state, render, ensureAccessToken);
        handleRefreshBidClick(e, startEl, state, render, ensureAccessToken, cfg, DEBUG);
        handleEditCloseTimeClick(e, startEl, state);
        handleEditEstimateClick(e, startEl, state);
        handleNextBidClick(e, startEl, state);
        handleDetailsClick(e, startEl, state);
        handleImagesClick(e, startEl, state);
        handleAuctionGroupToggleClick(e, startEl, render);
      },
      true
    );

    // Keyboard support for the focusable auction group header rows
    els.rows.addEventListener(
      'keydown',
      (e: KeyboardEvent) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        const startEl = getEventTarget(e);
        if (!startEl) return;
        handleAuctionGroupToggleClick(e, startEl, render);
      },
      true
    );
  }

  if (els.lotCardsContainer) {
    els.lotCardsContainer.addEventListener(
      'click',
      (e: Event) => {
        const startEl = getEventTarget(e);
        if (!startEl) return;

        handleEstimateButtonMobile(e, startEl, state);
        handleNextBidButtonMobile(e, startEl, state);
        handleDetailsButtonMobile(e, startEl, state);
      },
      true
    );
  }
}

function getEventTarget(e: Event): HTMLElement | null {
  let startEl: HTMLElement | null = null;

  if (e && typeof (e as any).composedPath === 'function') {
    const path = (e as any).composedPath();
    for (let i = 0; i < path.length; i++) {
      const node = path[i];
      if (node instanceof HTMLElement) {
        startEl = node;
        break;
      }
    }
  }

  if (!startEl) {
    const raw = e.target;
    startEl =
      raw && raw instanceof HTMLElement
        ? raw
        : raw && (raw as Node).parentElement
          ? (raw as Node).parentElement
          : null;
  }

  return startEl;
}

function handleAuctionGroupToggleClick(e: Event, startEl: HTMLElement, render: () => void): void {
  const headerRow = startEl.closest('[data-action="toggle-auction-group"]');
  if (!headerRow) return;

  const key = headerRow.getAttribute('data-group-key');
  if (!key) return;

  e.preventDefault();
  toggleGroupCollapsed(key);
  render();
}

function handleFavoriteClick(
  e: Event,
  startEl: HTMLElement,
  state: EventHandlerDeps['state'],
  render: () => void,
  ensureAccessToken: (retry?: boolean) => Promise<string | null>
): void {
  const favoriteButton = startEl.closest('[data-action="favorite"]');
  if (!favoriteButton) return;

  const id = favoriteButton.getAttribute('data-id');
  if (!id) return;

  const lot = state.lots.find((x: any) => x.id === id);
  if (!lot) return;

  e.preventDefault();

  if (state.pendingFavoriteIds && state.pendingFavoriteIds.has(id)) {
    return;
  }

  const newFavoriteState = !lot.favorite;
  if (!state.pendingFavoriteIds) {
    state.pendingFavoriteIds = new Set();
  }
  state.pendingFavoriteIds.add(id);
  // Try in-place update first, fall back to full render
  if (!updateFavoriteCellInPlace(id, lot)) {
    render();
  }

  syncFavoriteRemote(id, newFavoriteState, false, state, ensureAccessToken, false)
    .then(() => {
      state.pendingFavoriteIds!.delete(id);
      const lotIndex = state.lots.findIndex((x: any) => x.id === id);
      if (lotIndex >= 0) {
        state.lots[lotIndex].favorite = newFavoriteState;
      }
      // Try in-place update first, fall back to full render
      const updatedLot = state.lots.find((x: any) => x.id === id);
      if (!updatedLot || !updateFavoriteCellInPlace(id, updatedLot)) {
        render();
      }
      showToast(newFavoriteState ? 'Added to favorites' : 'Removed from favorites', {
        type: 'success',
      });
    })
    .catch((err: any) => {
      state.pendingFavoriteIds!.delete(id);
      // Try in-place update first, fall back to full render
      if (!updateFavoriteCellInPlace(id, lot)) {
        render();
      }
      showToast('Failed to update favorite: ' + (err?.message || String(err)), {
        type: 'error',
      });
    });
}

function handleDeleteClick(
  e: Event,
  startEl: HTMLElement,
  state: EventHandlerDeps['state'],
  render: () => void,
  ensureAccessToken: (retry?: boolean) => Promise<string | null>
): void {
  const deleteButton = startEl.closest('[data-action="delete"]');
  if (!deleteButton) return;

  const id = deleteButton.getAttribute('data-id');
  if (!id) return;

  const lot = state.lots.find((x: any) => x.id === id);
  if (!lot) return;

  e.preventDefault();
  handleDeleteLot(lot, state, render, applyRemoteSnapshot, ensureAccessToken, false);
}

/**
 * Update bid refresh button state in-place without full render.
 * Sets or clears the disabled state on the button.
 */
function updateBidButtonState(lotId: string, isPending: boolean): void {
  const button = document.querySelector(`button[data-action="refresh-bid"][data-id="${lotId}"]`);
  if (button instanceof HTMLButtonElement) {
    button.disabled = isPending;
  }
}

function handleRefreshBidClick(
  e: Event,
  startEl: HTMLElement,
  state: EventHandlerDeps['state'],
  render: () => void,
  ensureAccessToken: (retry?: boolean) => Promise<string | null>,
  cfg: any,
  DEBUG: boolean
): void {
  const refreshBidButton = startEl.closest('[data-action="refresh-bid"]');
  if (!refreshBidButton) return;

  const id = refreshBidButton.getAttribute('data-id');
  if (!id) return;

  const lot = state.lots.find((x: any) => x.id === id);
  if (!lot) return;

  e.preventDefault();

  if (!state.pendingBidRefreshIds) {
    state.pendingBidRefreshIds = new Set();
  }
  state.pendingBidRefreshIds.add(lot.id);

  // Update button state in-place instead of full render
  updateBidButtonState(lot.id, true);

  const ensureToken = async (retry?: boolean): Promise<string> => {
    const token = await ensureAccessToken(retry ?? false);
    if (!token) {
      throw new Error('Unable to acquire access token.');
    }
    return token;
  };

  refreshLotBidsRemote(
    lot.id,
    false,
    state,
    ensureToken,
    mapRemoteDocument,
    render,
    cfg?.debug || false
  )
    .then(() => {
      if (state.pendingBidRefreshIds) {
        state.pendingBidRefreshIds.delete(lot.id);
      }
      // Update button state in-place instead of full render
      updateBidButtonState(lot.id, false);
      showToast('Bid refreshed', { type: 'success' });
    })
    .catch((err: any) => {
      if (DEBUG) {
        console.error('bid-refresh-error', err);
      }

      if (state.pendingBidRefreshIds) {
        state.pendingBidRefreshIds.delete(lot.id);
      }

      // Update button state in-place instead of full render
      updateBidButtonState(lot.id, false);
      showToast('Failed to refresh bid: ' + (err?.message || String(err)), {
        type: 'error',
      });
    });
}

function handleEditCloseTimeClick(
  e: Event,
  startEl: HTMLElement,
  state: EventHandlerDeps['state']
): void {
  const closeTimeButton = startEl.closest('[data-action="edit-close-time"]');
  if (!closeTimeButton) return;

  const id = closeTimeButton.getAttribute('data-id');
  if (!id) return;

  const lot = state.lots.find((x: any) => x.id === id);
  if (!lot) return;

  e.preventDefault();

  setCustomCloseTime(getEffectiveCloseTime(lot));
  state.closeTimeEdit = { lotId: lot.id, closeTime: lot.closeTime }; // store old close time for later undo
  openCloseTimeModal();
}

function handleEditEstimateClick(
  e: Event,
  startEl: HTMLElement,
  state: EventHandlerDeps['state']
): void {
  const estimateButton = startEl.closest('[data-action="edit-estimate"]');
  if (!estimateButton) return;

  const id = estimateButton.getAttribute('data-id');
  if (!id) return;

  const lot = state.lots.find((x: any) => x.id === id);
  if (!lot) return;

  e.preventDefault();
  beginEstimateEdit(lot, state);
}

function handleNextBidClick(
  e: Event,
  startEl: HTMLElement,
  state: EventHandlerDeps['state']
): void {
  const nextBidButton = startEl.closest('[data-action="next-bid"]');
  if (!nextBidButton) return;

  const id = nextBidButton.getAttribute('data-id');
  if (!id) return;

  const lot = state.lots.find((x: any) => x.id === id);
  if (!lot || !lot.url) return;

  e.preventDefault();
  window.open(lot.url, '_blank', 'noopener,noreferrer');
}

function handleDetailsClick(
  e: Event,
  startEl: HTMLElement,
  state: EventHandlerDeps['state']
): void {
  const detailsButton = startEl.closest('[data-action="view-details"]');
  if (!detailsButton) return;

  const id = detailsButton.getAttribute('data-id');
  if (!id) return;

  const lot = state.lots.find((x: any) => x.id === id);
  if (!lot) return;

  e.preventDefault();
  openLotDetailsModal(lot);
}

function handleImagesClick(e: Event, startEl: HTMLElement, state: EventHandlerDeps['state']): void {
  const imagesButton = startEl.closest('[data-action="view-images"]');
  if (!imagesButton) return;

  const id = imagesButton.getAttribute('data-id');
  if (!id) return;

  const lot = state.lots.find((x: any) => x.id === id);
  if (!lot) return;

  e.preventDefault();
  openImagesModal(lot);
}

function handleEstimateButtonMobile(
  e: Event,
  startEl: HTMLElement,
  state: EventHandlerDeps['state']
): void {
  const estimateButton = startEl.closest('[data-action="edit-estimate-mobile"]');
  if (!estimateButton) return;

  e.preventDefault();
  e.stopPropagation();
  e.stopImmediatePropagation();

  const id = estimateButton.getAttribute('data-id');
  if (!id) return;

  const lot = state.lots.find((x: any) => x.id === id);
  if (!lot) return;

  beginEstimateEdit(lot, state);
}

function handleNextBidButtonMobile(
  e: Event,
  startEl: HTMLElement,
  state: EventHandlerDeps['state']
): void {
  const nextBidButton = startEl.closest('.lot-card__next-bid');
  if (!nextBidButton) return;

  e.preventDefault();
  e.stopPropagation();
  e.stopImmediatePropagation();

  const card = nextBidButton.closest('[data-id]');
  if (!card) return;

  const id = card.getAttribute('data-id');
  if (!id) return;

  const lot = state.lots.find((x: any) => x.id === id);
  if (!lot || !lot.url) return;

  window.open(lot.url, '_blank', 'noopener,noreferrer');
}

function handleDetailsButtonMobile(
  e: Event,
  startEl: HTMLElement,
  state: EventHandlerDeps['state']
): void {
  const cardTarget = startEl.closest('[data-action="view-details"]');
  if (!cardTarget) return;

  const id = cardTarget.getAttribute('data-id');
  if (!id) return;

  const lot = state.lots.find((x: any) => x.id === id);
  if (!lot) return;

  e.preventDefault();
  openLotDetailsModal(lot);
}
